import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Switch,
  TextField,
  Snackbar,
  Alert,
  Chip,
  InputAdornment,
  IconButton,
  Skeleton,
} from '@mui/material';
import {
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Visibility,
  VisibilityOff,
  Save as SaveIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import ActionButton from '../components/ActionButton';
import settingsService from '../services/settingsService';

const inputStyles = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: 'white',
    '& fieldset': { borderColor: '#E2E8F0' },
    '&:hover fieldset': { borderColor: '#4E83BD' },
    '&.Mui-focused fieldset': { borderColor: '#4E83BD' },
  },
  '& .MuiInputBase-input': { fontSize: '14px', color: '#1F2937' },
};

const Settings = () => {
  const [loading, setLoading] = useState(true);
  const [isLocked, setIsLocked] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [updatedByEmail, setUpdatedByEmail] = useState(null);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Load site lock settings
  useEffect(() => {
    const fetchSettings = async () => {
      const result = await settingsService.getSiteLock();
      if (result.success) {
        setIsLocked(result.data.is_locked);
        setUpdatedAt(result.data.updated_at);
        setUpdatedByEmail(result.data.updated_by_email);
        // Don't set password - it comes masked from the API
      } else {
        showSnackbar(result.error, 'error');
      }
      setLoading(false);
    };
    fetchSettings();
  }, []);

  const handleSave = async () => {
    const payload = { is_locked: isLocked };
    if (password.trim()) {
      payload.password = password;
    }

    // Validate: if enabling lock, password must be provided (or already set)
    if (isLocked && !password.trim()) {
      showSnackbar('Please set a tester password when enabling site lock', 'warning');
      return;
    }

    const result = await settingsService.updateSiteLock(payload);
    if (result.success) {
      showSnackbar('Site lock settings updated successfully');
      setUpdatedAt(result.data.updated_at);
      setUpdatedByEmail(result.data.updated_by_email);
      setPassword(''); // Clear password field after save
    } else {
      showSnackbar(result.error, 'error');
    }
  };

  if (loading) {
    return (
      <Box sx={{ padding: { xs: 2, sm: 2.5, md: 3 }, backgroundColor: '#F8FAFC', minHeight: '100vh' }}>
        <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 2, mb: 3, maxWidth: 400 }} />
        <Skeleton variant="rectangular" height={320} sx={{ borderRadius: 2, maxWidth: 640 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ padding: { xs: 2, sm: 2.5, md: 3 }, backgroundColor: '#F8FAFC', minHeight: '100vh' }}>
      {/* Page Header - matches Dashboard, Reports, Subscription */}
      <Paper
        elevation={0}
        sx={{
          padding: { xs: 1.5, sm: 2, md: 2.5 },
          marginBottom: { xs: 2, md: 3 },
          borderRadius: 2,
          background: 'linear-gradient(135deg, #4E83BD 0%, #3A6A99 100%)',
          border: '1px solid #ECF0F1',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <SettingsIcon sx={{ color: 'white', fontSize: 28 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '18px', sm: '20px' }, color: 'white' }}>
            Settings
          </Typography>
        </Box>
      </Paper>

      {/* Site Lock Section - card style like Reports/Policies content */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: '1px solid #E2E8F0',
          overflow: 'hidden',
          maxWidth: 640,
        }}
      >
        <Box sx={{ padding: { xs: 2, sm: 2.5, md: 3 }, backgroundColor: 'white' }}>
          {/* Section header with icon - like Dashboard sections */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 1.5 }}>
            {isLocked ? (
              <LockIcon sx={{ color: '#DC2626', fontSize: 24 }} />
            ) : (
              <LockOpenIcon sx={{ color: '#059669', fontSize: 24 }} />
            )}
            <Typography variant="h6" sx={{ color: '#1E293B', fontSize: { xs: '14px', sm: '16px' }, fontWeight: 700 }}>
              Site Lock / Tester Gate
            </Typography>
            <Chip
              label={isLocked ? 'Locked' : 'Unlocked'}
              size="small"
              sx={{
                ml: 'auto',
                backgroundColor: isLocked ? '#FEE2E2' : '#D1FAE5',
                color: isLocked ? '#DC2626' : '#059669',
                fontWeight: 600,
              }}
            />
          </Box>

          <Typography variant="body2" sx={{ color: '#64748B', marginBottom: 3, lineHeight: 1.6 }}>
            When enabled, the user-facing frontend will show a password wall. Only testers who know the
            shared password can access the app. Useful for pre-launch testing.
          </Typography>

          {/* Toggle row */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 2.5,
              padding: 2,
              borderRadius: 1.5,
              backgroundColor: '#F8FAFC',
              border: '1px solid #E2E8F0',
            }}
          >
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 600, color: '#1E293B' }}>
                Enable Site Lock
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748B', display: 'block', mt: 0.25 }}>
                Block public access with a tester password
              </Typography>
            </Box>
            <Switch
              checked={isLocked}
              onChange={(e) => setIsLocked(e.target.checked)}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: '#4E83BD' },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4E83BD' },
              }}
            />
          </Box>

          {/* Password Field */}
          <TextField
            fullWidth
            size="small"
            label="Tester Password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={isLocked ? 'Enter new password' : 'Set a password for when lock is enabled'}
            helperText={isLocked ? 'Required when lock is enabled' : 'Optional — set for future use'}
            sx={{ mb: 3, ...inputStyles }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small" aria-label="toggle password visibility">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Save Button - same style as Policies/FAQ/Subscription/Communications primary action */}
          <ActionButton
            variant="contained"
            onClick={handleSave}
            loadingText="Saving..."
            startIcon={<SaveIcon />}
            sx={{
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 600,
              background: 'linear-gradient(135deg, #4E83BD 0%, #3A6A99 100%)',
              color: 'white',
              borderRadius: '10px',
              padding: '12px 24px',
              boxShadow: '0 4px 12px rgba(78, 131, 189, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #3A6A99 0%, #2D5580 100%)',
                boxShadow: '0 6px 16px rgba(78, 131, 189, 0.4)',
              },
            }}
          >
            Save Changes
          </ActionButton>

          {/* Last Updated */}
          {updatedAt && (
            <Typography variant="caption" sx={{ display: 'block', mt: 2, color: '#64748B' }}>
              Last updated: {new Date(updatedAt).toLocaleString()}
              {updatedByEmail && ` by ${updatedByEmail}`}
            </Typography>
          )}
        </Box>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings;
